// VOID Conflict / UCG — Community Transmissions & Data Feed Modal
// Clean, diegetic Sci-Fi dialog for developer messages, patch notes, and gift claims.

import { inboxManager } from '../net/inboxManager.js';
import { getIconSvg } from './icons.js';

let activeOverlay = null;

function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr.replace(' ', 'T') + 'Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoStr.substring(0, 10);
  }
}

export async function showDataFeedModal(game) {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const audio = game?.audio;
  const messages = await inboxManager.fetchMessages();

  // Mark all unread messages as read upon viewing the data feed
  for (const m of messages) {
    if (!m.isRead) {
      inboxManager.markAsRead(m.id);
    }
  }

  const layer = document.createElement('div');
  layer.id = 'data-feed-fullscreen-overlay';
  layer.className = 'fullscreen-dialog-overlay active';
  layer.style.zIndex = '99990';

  const renderContent = () => {
    let listHtml = '';
    if (!messages || messages.length === 0) {
      listHtml = `
        <div class="data-feed-empty-state">
          NO ACTIVE TRANSMISSIONS LOGGED // SUB-SPACE SENSORS QUIET
        </div>
      `;
    } else {
      listHtml = messages.map(m => {
        const catUpper = (m.category || 'announcement').toUpperCase().replace('_', ' ');
        const dateStr = formatDate(m.createdAt);

        let rewardsMarkup = '';
        if (m.rewards) {
          const rewardItems = [];
          if (m.rewards.fugazi) {
            rewardItems.push(`
              <div class="data-feed-reward-item fugazi" title="Fugazi">
                <span class="reward-glyph">⬡</span>
                <span class="reward-qty">+${m.rewards.fugazi}</span>
                <span class="reward-unit">FUGAZI</span>
              </div>
            `);
          }
          if (m.rewards.tickets) {
            rewardItems.push(`
              <div class="data-feed-reward-item tickets" title="Spin Tickets">
                <span class="reward-glyph">${getIconSvg('ticket', 12)}</span>
                <span class="reward-qty">+${m.rewards.tickets}</span>
                <span class="reward-unit">TICKETS</span>
              </div>
            `);
          }
          if (m.rewards.packs) {
            rewardItems.push(`
              <div class="data-feed-reward-item packs" title="Booster Packs">
                <span class="reward-glyph">${getIconSvg('crate', 12)}</span>
                <span class="reward-qty">+${m.rewards.packs}</span>
                <span class="reward-unit">BOOSTER</span>
              </div>
            `);
          }

          const claimButton = m.isClaimed
            ? `<div class="data-feed-claimed-pill">✓ CLAIMED</div>`
            : `<button type="button" class="data-feed-claim-btn" data-msg-id="${m.id}">CLAIM</button>`;

          rewardsMarkup = `
            <div class="data-feed-reward-strip">
              <div class="data-feed-reward-items">
                ${rewardItems.join('')}
              </div>
              <div class="data-feed-reward-action">
                ${claimButton}
              </div>
            </div>
          `;
        }

        const isUnreadPill = !m.isRead
          ? `<span class="data-feed-unread-dot" title="New transmission"></span>`
          : '';

        const isGift = m.category === 'gift' || !!m.rewards;

        return `
          <article class="data-feed-card ${isGift ? 'is-gift' : ''} ${m.isClaimed ? 'card-claimed' : ''}">
            <div class="data-feed-card-header">
              <div class="data-feed-card-tags">
                ${isUnreadPill}
                <span class="data-feed-cat-badge cat-${m.category}">${catUpper}</span>
              </div>
              <time class="data-feed-date">${dateStr}</time>
            </div>

            <h3 class="data-feed-card-title">${escapeHtml(m.title)}</h3>
            <div class="data-feed-card-content">${escapeHtml(m.content).replace(/\n/g, '<br>')}</div>

            ${rewardsMarkup}
          </article>
        `;
      }).join('');
    }

    layer.innerHTML = `
      <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
        <div class="fullscreen-dialog-bg-image"></div>
        <div class="fullscreen-dialog-scrim"></div>
        <div class="fullscreen-dialog-vignette"></div>
      </div>

      <div class="fullscreen-dialog-container data-feed-container">
        <!-- Top Navigation Bar -->
        <header class="fullscreen-dialog-header data-feed-header">
          <div class="fullscreen-dialog-header-left">
            <div class="fullscreen-dialog-category">FEED</div>
            <h2 class="fullscreen-dialog-title">DATA &amp; GIFTS</h2>
          </div>
          <div class="fullscreen-dialog-header-actions data-feed-header-actions">
            <div class="fullscreen-dialog-status-pill status-pill-circular" title="Sub-space Comms Active">
              <span class="status-indicator-dot" style="background: #00f0ff; box-shadow: 0 0 8px #00f0ff;"></span>
            </div>
            <button type="button" class="data-feed-close-icon-btn modal-close-icon-btn" id="data-feed-header-close-btn" aria-label="Close" title="Close (Esc)">
              <svg class="data-feed-close-svg dialog-close-svg" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="2.5" y1="2.5" x2="11.5" y2="11.5" />
                <line x1="11.5" y1="2.5" x2="2.5" y2="11.5" />
              </svg>
            </button>
          </div>
        </header>

        <div class="fullscreen-dialog-body data-feed-body">
          <div class="fullscreen-card-panel data-feed-panel">
            
            <!-- Transmissions Stream -->
            <div class="data-feed-stream">
              ${listHtml}
            </div>

            <div class="data-feed-retention-note">
              TRANSFERS RETENTION: 14 DAYS // DEPT. OF ORDNANCE
            </div>
          </div>
        </div>

        <footer class="fullscreen-dialog-footer data-feed-footer">
          <button type="button" class="fullscreen-return-btn" id="data-feed-close-btn">
            <span class="start-btn-text">RETURN</span>
          </button>
        </footer>
      </div>
    `;

    bindEvents();
  };

  const bindEvents = () => {
    // Close modal (both bottom return button and top-right header close button)
    const closeBtn = layer.querySelector('#data-feed-close-btn');
    closeBtn?.addEventListener('click', () => {
      audio?.playUiClick?.();
      closeModal();
    });

    const headerCloseBtn = layer.querySelector('#data-feed-header-close-btn');
    headerCloseBtn?.addEventListener('click', () => {
      audio?.playUiClick?.();
      closeModal();
    });

    // Claim buttons
    layer.querySelectorAll('.data-feed-claim-btn:not(.claimed)').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const msgId = parseInt(btn.dataset.msgId, 10);
        if (!msgId) return;

        btn.disabled = true;
        btn.textContent = 'AUTHORIZING...';

        const res = await inboxManager.claimReward(msgId);
        if (res.ok) {
          audio?.playDailyJackpotFanfare?.();
          const cardEl = btn.closest('.data-feed-card');
          if (cardEl) {
            cardEl.classList.add('card-claimed', 'claim-flash-pulse');
          }
          const claimWord = getRandomClaimWord();
          btn.outerHTML = `<div class="data-feed-claimed-pill">✓ ${claimWord}</div>`;

          // Floating reward badge effect
          showRewardToast(res.rewards, claimWord);
        } else {
          audio?.playUiClick?.();
          btn.disabled = false;
          btn.textContent = 'RETRY';
          alert(res.message || 'Failed to claim allocation.');
        }
      });
    });
  };

  const closeModal = () => {
    layer.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
    window.removeEventListener('keydown', handleKey);
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  };
  window.addEventListener('keydown', handleKey);

  renderContent();
  document.body.appendChild(layer);
  activeOverlay = layer;
}

// Pool of in-universe, one-word Sci-Fi synonyms for "CLAIMED"
export const SCI_FI_CLAIM_SYNONYMS = [
  'CLAIMED',
  'SECURED',
  'ACQUIRED',
  'RETRIEVED',
  'TRANSFERRED',
  'REQUISITIONED',
  'INTERCEPTED',
  'CONFIRMED',
  'PROVISIONED',
  'SYNCHRONIZED'
];

export function getRandomClaimWord() {
  return SCI_FI_CLAIM_SYNONYMS[Math.floor(Math.random() * SCI_FI_CLAIM_SYNONYMS.length)];
}

export function showRewardToast(rewards, claimWord = null) {
  if (!rewards) return;
  const title = claimWord || getRandomClaimWord();

  const rows = [];
  if (rewards.fugazi) {
    rows.push(`
      <div class="data-feed-toast-row fugazi">
        <span class="toast-row-glyph">⬡</span>
        <span class="toast-row-amount">${rewards.fugazi}</span>
        <span class="toast-row-unit">Fugazi</span>
      </div>
    `);
  }
  if (rewards.tickets) {
    rows.push(`
      <div class="data-feed-toast-row tickets">
        <span class="toast-row-glyph">${getIconSvg('ticket', 14)}</span>
        <span class="toast-row-amount">${rewards.tickets}</span>
        <span class="toast-row-unit">Tickets</span>
      </div>
    `);
  }
  if (rewards.packs) {
    const unit = rewards.packs === 1 ? 'Booster' : 'Boosters';
    rows.push(`
      <div class="data-feed-toast-row packs">
        <span class="toast-row-glyph">${getIconSvg('crate', 14)}</span>
        <span class="toast-row-amount">${rewards.packs}</span>
        <span class="toast-row-unit">${unit}</span>
      </div>
    `);
  }

  if (rows.length === 0) return;

  const toast = document.createElement('div');
  toast.className = 'data-feed-toast';
  toast.innerHTML = `
    <div class="data-feed-toast-header">
      <span class="data-feed-toast-beacon" aria-hidden="true"></span>
      <span class="data-feed-toast-title">${title}</span>
    </div>
    <div class="data-feed-toast-body">
      ${rows.join('')}
    </div>
  `;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translate(-50%, -24px) scale(0.96)';
    setTimeout(() => toast.remove(), 400);
  }, 3200);
}
